import { DependencyList, useCallback, useEffect, useRef, useState } from "react";
import { usePoll } from "./usePoll";

/**
 * The console's one data-fetching shape: load on mount and when deps change, expose reload.
 *
 * Deliberately tiny instead of pulling in a query library — the console's needs are "show it,
 * refresh it, say when it broke", and every page behaving identically matters more than caching.
 *
 * Pass `pollMs` for anything that goes stale on its own (heartbeats, clip progress, the event
 * feed). Those refreshes are quiet: they swap the data in without flipping `isLoading`, so a table
 * someone is reading never blinks back to a spinner underneath them.
 */
export function useLoad<T>(fn: () => Promise<T>, deps: DependencyList, options?: { pollMs?: number }) {
    const [data, setData] = useState<T>();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string>();
    const seq = useRef(0);

    // The caller rebuilds `fn` every render; `deps` is what decides when it is worth re-running.
    const fnRef = useRef(fn);
    fnRef.current = fn;

    const run = useCallback(async (quiet: boolean) => {
        const mySeq = ++seq.current;
        if (!quiet) setIsLoading(true);
        try {
            const result = await fnRef.current();
            if (seq.current === mySeq) {
                setData(result);
                setError(undefined);
            }
        } catch (err) {
            if (seq.current === mySeq) setError(err instanceof Error ? err.message : "Something went wrong");
        } finally {
            if (seq.current === mySeq) setIsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);

    const reload = useCallback(() => run(false), [run]);

    useEffect(() => {
        run(false);
    }, [run]);

    usePoll(() => run(true), options?.pollMs);

    return { data, isLoading, error, reload };
}
